import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheckCircle, 
  faExclamationTriangle, 
  faSpinner,
  faCreditCard,
  faCogs
} from '@fortawesome/free-solid-svg-icons';
import { faPaypal } from '@fortawesome/free-brands-svg-icons';
import { woocommerceService } from '../../../services/woocommerceService';

const CheckerContainer = styled.div`
  max-width: 600px;
  margin: 2rem auto;
  padding: 2rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h2`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  color: #333;
`;

const PaymentMethodItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  margin-bottom: 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  
  &.enabled {
    border-color: #28a745;
    background-color: #f8fff9;
  }
  
  &.disabled {
    border-color: #dc3545;
    background-color: #fff8f8;
  }
`;

const MethodIcon = styled.div`
  font-size: 1.5rem;
  
  &.enabled {
    color: #28a745;
  }
  
  &.disabled {
    color: #dc3545;
  }
  
  &.loading {
    color: #6c757d;
  }
`;

const MethodInfo = styled.div`
  flex: 1;
`;

const MethodName = styled.h4`
  margin: 0 0 0.25rem 0;
  color: #333;
`;

const MethodDescription = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: #666;
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  
  &.enabled {
    background-color: #28a745;
    color: white;
  }
  
  &.disabled {
    background-color: #dc3545;
    color: white;
  }
  
  &.loading {
    background-color: #6c757d;
    color: white;
  }
`;

const Instructions = styled.div`
  margin-top: 2rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #007cba;
`;

const InstructionTitle = styled.h3`
  margin: 0 0 1rem 0;
  color: #333;
`;

const InstructionList = styled.ol`
  margin: 0;
  padding-left: 1.5rem;
  
  li {
    margin-bottom: 0.5rem;
  }
`;

const PaymentMethodChecker = () => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkPaymentMethods();
  }, []);

  const checkPaymentMethods = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Try to get payment methods
      const methods = await woocommerceService.getPaymentMethods();
      
      // Format payment methods for display
      const formattedMethods = methods.map(method => ({
        id: method.id || method.method_id || method.gateway_id,
        name: method.title || method.name || method.method_title,
        description: method.description || method.method_description || '',
        enabled: method.enabled === true || method.enabled === 'yes',
        icon: getMethodIcon(method.id || method.method_id || method.gateway_id)
      }));
      
      setPaymentMethods(formattedMethods);
      
    } catch (err) {

      setError(err.message);
      
      // Show default expected methods with unknown status
      setPaymentMethods([
        { id: 'stripe', name: 'Stripe Credit Card', description: 'Pay with credit/debit card', enabled: null, icon: faCreditCard },
        { id: 'woocommerce_payments', name: 'WooCommerce Payments', description: 'WooCommerce built-in payments', enabled: null, icon: faCreditCard },
        { id: 'paypal', name: 'PayPal', description: 'Pay with PayPal account', enabled: null, icon: faPaypal },
        { id: 'bacs', name: 'Bank Transfer', description: 'Direct bank transfer', enabled: null, icon: faCogs },
        { id: 'cod', name: 'Cash on Delivery', description: 'Pay when you receive', enabled: null, icon: faCogs }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const getMethodIcon = (methodId) => {
    switch (methodId?.toLowerCase()) {
      case 'stripe':
      case 'stripe_cc':
      case 'woocommerce_payments':
        return faCreditCard;
      case 'paypal':
        return faPaypal;
      default:
        return faCogs;
    }
  };

  const getStatusIcon = (enabled) => {
    if (enabled === null) return faSpinner;
    return enabled ? faCheckCircle : faExclamationTriangle;
  };

  const getStatusClass = (enabled) => {
    if (enabled === null) return 'loading';
    return enabled ? 'enabled' : 'disabled';
  };

  const getStatusText = (enabled) => {
    if (enabled === null) return 'Unknown';
    return enabled ? 'Enabled' : 'Disabled';
  };

  const hasEnabledStripeMethod = paymentMethods.some(method => 
    (method.id?.includes('stripe') || method.id?.includes('woocommerce_payments')) && 
    method.enabled === true
  );

  return (
    <CheckerContainer>
      <Title>
        <FontAwesomeIcon icon={faCreditCard} />
        Payment Methods Status
      </Title>

      {loading && (
        <PaymentMethodItem>
          <MethodIcon className="loading">
            <FontAwesomeIcon icon={faSpinner} spin />
          </MethodIcon>
          <MethodInfo>
            <MethodName>Loading payment methods...</MethodName>
            <MethodDescription>Checking available payment options</MethodDescription>
          </MethodInfo>
        </PaymentMethodItem>
      )}

      {error && (
        <PaymentMethodItem className="disabled">
          <MethodIcon className="disabled">
            <FontAwesomeIcon icon={faExclamationTriangle} />
          </MethodIcon>
          <MethodInfo>
            <MethodName>Error Loading Payment Methods</MethodName>
            <MethodDescription>{error}</MethodDescription>
          </MethodInfo>
          <StatusBadge className="disabled">Error</StatusBadge>
        </PaymentMethodItem>
      )}

      {!loading && paymentMethods.map(method => (
        <PaymentMethodItem key={method.id} className={getStatusClass(method.enabled)}>
          <MethodIcon className={getStatusClass(method.enabled)}>
            <FontAwesomeIcon icon={getStatusIcon(method.enabled)} />
          </MethodIcon>
          <MethodInfo>
            <MethodName>{method.name}</MethodName>
            <MethodDescription>{method.description}</MethodDescription>
          </MethodInfo>
          <StatusBadge className={getStatusClass(method.enabled)}>
            {getStatusText(method.enabled)}
          </StatusBadge>
        </PaymentMethodItem>
      ))}

      <Instructions>
        <InstructionTitle>
          {hasEnabledStripeMethod ? '✅ Stripe is Configured!' : '❌ Setup Required'}
        </InstructionTitle>
        
        {hasEnabledStripeMethod ? (
          <p>Great! Stripe payment method is enabled and should work for checkout.</p>
        ) : (
          <div>
            <p>To enable Stripe payments, follow these steps:</p>
            <InstructionList>
              <li>Go to WordPress Admin Dashboard</li>
              <li>Navigate to <strong>WooCommerce → Settings → Payments</strong></li>
              <li>Install <strong>"WooCommerce Stripe Gateway"</strong> plugin if not installed</li>
              <li>Enable the Stripe payment method</li>
              <li>Enter your Stripe API keys (publishable and secret keys)</li>
              <li>Set to <strong>Test Mode</strong> for testing</li>
              <li>Save settings and test the payment</li>
            </InstructionList>
          </div>
        )}
      </Instructions>
    </CheckerContainer>
  );
};

export default PaymentMethodChecker;